import { apiGet, apiPut, apiPost, apiDelete } from './client';
import { ApiResponse } from '@tinyrewards/shared';

export interface PortfolioItem {
    issuedBadgeID: string;
    badgeID: string;
    badgeName: string;
    badgeDescription: string | null;
    badgeCriteria: string | null;
    issuerName: string;
    badgeImage: string | null;
    tags: string[];
    isPublic: boolean;
    issuedDate: string;
    expiresOn: string | null;
    isSharedToProfile: boolean;
    endorsementCount: number;
    evidenceCount: number;
    /** Stored Pages.Type — 'learningpath' for Multi Badges. Drives the stacked-card frame. */
    badgeType?: string | null;
}

export interface PortfolioEndorsement {
    endorsementID: string;
    endorserName: string;
    endorserOrganization: string | null;
    endorsementText: string;
    dateCreated: string;
}

export interface BadgeEvidence {
    ID: string;
    evidenceURL: string | null;
    evidenceText: string | null;
    EvidenceFileName: string | null;
    EvidenceOriginalFileName: string | null;
    LastUpdatedDate: string;
}

export type PortfolioResponse = ApiResponse<PortfolioItem[]> & {
    total: number;
    page: number;
    pageSize: number;
};

export function getPortfolio(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    sort?: 'issuedDate' | 'name';
    order?: 'asc' | 'desc';
}): Promise<PortfolioResponse> {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.search) q.set('search', params.search);
    if (params.sort) q.set('sort', params.sort);
    if (params.order) q.set('order', params.order);
    return apiGet<PortfolioItem[]>(`/api/portfolio?${q.toString()}`) as Promise<PortfolioResponse>;
}

export function toggleShare(issuedBadgeId: string): Promise<ApiResponse<{ isSharedToProfile: boolean }>> {
    return apiPut<{ isSharedToProfile: boolean }>(`/api/portfolio/${issuedBadgeId}/toggle-share`, {});
}

export function revokePortfolioBadge(issuedBadgeId: string): Promise<ApiResponse<null>> {
    return apiDelete<null>(`/api/rewards/${issuedBadgeId}`);
}

export function getPortfolioEndorsements(badgeId: string): Promise<ApiResponse<PortfolioEndorsement[]>> {
    return apiGet<PortfolioEndorsement[]>(`/api/portfolio/${badgeId}/endorsements`);
}

export function getPortfolioEvidence(issuedBadgeId: string): Promise<ApiResponse<{ evidences: BadgeEvidence[] }>> {
    return apiGet<{ evidences: BadgeEvidence[] }>(`/api/evidence/badge/${issuedBadgeId}`);
}

export function addEvidence(payload: {
    openBadgeIssueID: string;
    evidenceURL?: string;
    evidenceText?: string;
}): Promise<ApiResponse<void>> {
    return apiPost<void>('/api/evidence', payload);
}

export function deleteEvidence(evidenceId: string, key: 'url' | 'text'): Promise<ApiResponse<void>> {
    return apiDelete<void>(`/api/evidence/${evidenceId}?key=${key}`);
}

export function requestEndorsementForBadge(payload: {
    badgeID: string;
    endorserEmail: string;
    requestMessage?: string;
}): Promise<ApiResponse<void>> {
    return apiPost<void>('/api/endorsements/request', payload);
}
